import { toast } from '../components/Toast';
import { resetPassword } from '../api/auth.api';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = 'Reset Password | Fone Express';
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.newPassword) {
            newErrors.newPassword = 'Password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one uppercase letter';
        } else if (!/[a-z]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one lowercase letter';
        } else if (!/\d/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one digit';
        } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one special character';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        if (!token) {
            toast.error('Invalid or missing reset token');
            return;
        }

        setSubmitting(true);
        try {
            const res = await resetPassword({ token, password: formData.newPassword });
            if (res.data?.isOk) {
                toast.success('Password reset successfully. Please sign in.');
                navigate('/login', { replace: true });
            } else {
                toast.error(res.data?.message || 'Failed to reset password');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Invalid or expired token. Please request a new reset link.');
        } finally {
            setSubmitting(false);
        }
    };

    // Show error if no token
    if (!token) {
        return (
            <div className="min-h-screen bg-muted flex items-center justify-center px-4 pt-[10px] pb-16">
                <div className="w-full max-w-md">
                    <div className="bg-card rounded-xl shadow-sm border border-border p-8 text-center">
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-error-warning-line text-2xl text-red-500"></i>
                        </div>
                        <h1 className="text-xl font-bold text-foreground mb-2">Invalid Reset Link</h1>
                        <p className="text-muted-foreground text-sm mb-6">
                            This password reset link is invalid or has expired. Please request a new one.
                        </p>
                        <Link
                            to="/forgot-password"
                            className="inline-block px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                        >
                            Request New Link
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center px-4 pt-[10px] pb-16">
            <div className="w-full max-w-md">
                <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-lock-password-line text-2xl text-primary"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground mb-2">Reset Password</h1>
                        <p className="text-muted-foreground text-sm">Enter your new password below.</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* New Password */}
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-1.5">
                                New Password
                            </label>
                            <div className="relative">
                                <input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    placeholder="Min 8 characters"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.newPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {errors.newPassword ? (
                                <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>
                            ) : (
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Must include uppercase, lowercase, digit, and special character
                                </p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1.5">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Re-enter your new password"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.confirmPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting && (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            )}
                            {submitting ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        <Link to="/login" className="text-primary font-medium hover:underline">
                            Back to Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
